// ─── Multi-language invitation utilities ──────────────────────
// Helpers to render and edit invitation content in multiple languages.
// Non-invasive overlay model: original content stays in v2_content (in the
// event's default language), and v2_translations[lang] holds an overlay of
// the same shape with translated string values.

export const LANGUAGE_OPTIONS = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'pt', name: 'Português-PT', flag: '🇵🇹' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'el', name: 'Ελληνικά', flag: '🇬🇷' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
];

export const findLanguageOption = (code) =>
  LANGUAGE_OPTIONS.find((o) => o.code === code) || { code, name: code.toUpperCase(), flag: '' };

/**
 * Auto-detect best language for a guest given the languages enabled on the event.
 * Priority: ?lang=XX URL param > saved preference > navigator.language match > fallback.
 */
export function detectGuestLanguage(availableLangs, fallback = 'en', slug = '') {
  if (!Array.isArray(availableLangs) || availableLangs.length < 2) return fallback;
  // 1. URL ?lang= param
  try {
    const url = new URL(window.location.href);
    const qp = (url.searchParams.get('lang') || '').toLowerCase();
    if (qp && availableLangs.includes(qp)) return qp;
  } catch { /* */ }
  // 2. localStorage preference (per event slug)
  try {
    const saved = localStorage.getItem(`sitoa_lang_${slug || 'default'}`);
    if (saved && availableLangs.includes(saved)) return saved;
  } catch { /* */ }
  // 3. Browser language (match prefix, e.g. 'pt-BR' → 'pt')
  try {
    const browserLangs = (navigator.languages || [navigator.language || 'en'])
      .map((l) => (l || '').toLowerCase().split('-')[0]);
    for (const bl of browserLangs) {
      if (availableLangs.includes(bl)) return bl;
    }
  } catch { /* */ }
  // 4. Fallback to event default
  return availableLangs.includes(fallback) ? fallback : availableLangs[0];
}

export function saveGuestLanguagePreference(slug, lang) {
  try {
    localStorage.setItem(`sitoa_lang_${slug || 'default'}`, lang);
  } catch { /* */ }
}

/**
 * Structural keys that an overlay must NEVER override. Translation
 * overlays should only mutate text content. If the LLM accidentally
 * "translates" an element id, layout, or coordinate (or any of these
 * accumulated through historical bugs), we strip them here so the
 * rendered output keeps the canonical base structure.
 *
 * Why: language drift bug — Portuguese kept rendering an OLD image
 * element / coordinates because the overlay had stale structural
 * data while English had been updated. Layout is a canvas concept,
 * not a language concept.
 */
const STRUCTURAL_KEYS = new Set([
  'id', 'type', 'variant', 'layout', 'background',
  // NOTE: `_elements` removed — we recurse into element arrays so
  // text fields (name, label, text, caption) inside each element CAN
  // be translated. Structural keys below still block layout/src
  // drift on a per-element basis.
  'x', 'y', 'w', 'h', 'z', 'width', 'height',
  'top', 'left', 'right', 'bottom',
  'rotation', 'rotate', 'transform', 'transformOrigin', 'scale',
  'customStyle', 'styleId',
  'sectionId', 'elementId',
  'src', 'url', 'image', 'imageUrl', 'mediaUrl',
  'fit', 'objectFit', 'objectPosition',
  'visible', 'enabled', 'hidden',
  'opacity', 'zIndex', 'role',
]);

/**
 * Deep-merge a translation overlay onto base content. The overlay only
 * contains string fields that have been translated; everything else falls
 * through to the base value.
 *
 * Cases:
 *   - overlay == null/undefined           → base unchanged
 *   - base == null/undefined              → overlay value
 *   - base is primitive, overlay primitive → overlay wins if non-empty
 *   - base is primitive, overlay is object → shape mismatch, keep base
 *   - base is array, overlay is array     → index-wise recursive merge
 *   - base is object, overlay is object   → recurse per overlay key
 *   - base is object, overlay is primitive → shape mismatch, keep base
 *
 * Structural keys (see STRUCTURAL_KEYS) are NEVER overlaid — they always
 * use the base value regardless of what the overlay contains.
 */
export function applyTranslations(base, overlay) {
  if (overlay == null) return base;
  if (base == null) return overlay;
  // Primitive base — overlay wins if it's also a primitive and non-empty.
  // (If overlay is an object/array, that's a shape mismatch — keep base.)
  if (typeof base !== 'object') {
    if (typeof overlay === 'object') return base;
    return (overlay !== undefined && overlay !== null && overlay !== '') ? overlay : base;
  }
  // Array base
  if (Array.isArray(base)) {
    if (!Array.isArray(overlay)) return base;
    return base.map((item, i) => applyTranslations(item, overlay[i]));
  }
  // Object base — overlay must also be object
  if (typeof overlay !== 'object' || Array.isArray(overlay)) return base;
  const out = { ...base };
  for (const key of Object.keys(overlay)) {
    // Structural fields: layout, id, coordinates, image src, etc.
    // The overlay simply doesn't get a say here. Translation overlays
    // are text-only by contract.
    if (STRUCTURAL_KEYS.has(key)) continue;
    if (key in base) {
      out[key] = applyTranslations(base[key], overlay[key]);
    } else {
      // Overlay-only key with no base counterpart. Only allow primitives
      // (translated text additions) — never structural blobs the LLM
      // hallucinated.
      const v = overlay[key];
      if (typeof v !== 'object' || v === null) {
        out[key] = v;
      } else if (Array.isArray(v)) {
        // Skip — can't safely add a structural array with no base.
        continue;
      } else {
        // Recursively merge against an empty object to apply the same
        // structural-key protection inside this branch.
        out[key] = applyTranslations({}, v);
      }
    }
  }
  return out;
}

/**
 * Resolve invitation content for a given language.
 * v2_content = original content (default language)
 * v2_translations = { it: { v2_content: {...}, story_text: "...", timeline: [...], ... } }
 * activeLang = target language code
 * defaultLang = event's default language
 *
 * Returns the overlay-applied v2_content. The overlay may also contain v1 fields
 * (story_text, timeline, etc.) — use resolveV1Field() to read those.
 */
export function resolveInvitationContent(v2_content, v2_translations, activeLang, defaultLang) {
  if (!v2_content) return v2_content;
  if (!activeLang || activeLang === defaultLang) return v2_content;
  const overlayBag = (v2_translations || {})[activeLang];
  if (!overlayBag) return v2_content;
  // Overlay may have `v2_content` key or the older flat shape — support both
  const overlayContent = overlayBag.v2_content !== undefined ? overlayBag.v2_content : overlayBag;
  return applyTranslations(v2_content, overlayContent);
}

/**
 * Resolve a v1 template field (e.g. rsvpPage.story_text) with an optional translation.
 */
export function resolveV1Field(rsvpPage, fieldName, v2_translations, activeLang, defaultLang) {
  if (!rsvpPage) return undefined;
  const base = rsvpPage[fieldName];
  if (!activeLang || activeLang === defaultLang) return base;
  const overlay = (v2_translations || {})[activeLang];
  if (!overlay) return base;
  const tx = overlay[fieldName];
  if (tx === undefined || tx === null) return base;
  return applyTranslations(base, tx);
}

/**
 * Phase Lang-A (Option A) — resolve `settingsOverrides` with an optional
 * translation overlay applied to its `dynamicSections[i].data` subtree.
 *
 * Overlays generated by the LLM crawler are written under
 *   v2_translations[lang].v2_settings_overrides.dynamicSections[i].data.<path>
 *
 * No-op when activeLang is the default language or no overlay exists. Falls
 * through untouched for non-dynamicSections fields on settingsOverrides.
 */
export function resolveSettingsOverrides(settingsOverrides, v2_translations, activeLang, defaultLang) {
  const base = settingsOverrides || {};
  if (!activeLang || activeLang === defaultLang) return base;
  const overlayBag = (v2_translations || {})[activeLang];
  if (!overlayBag) return base;
  const overlayContent = overlayBag.v2_content !== undefined ? overlayBag : overlayBag;
  const ov = (overlayContent.v2_settings_overrides || {});
  const dynOverlay = ov.dynamicSections;
  const dynBase = base.dynamicSections;
  if (!Array.isArray(dynOverlay) || !Array.isArray(dynBase)) return base;
  // Index-wise merge — preserves all base fields, overlays translated string leaves.
  const mergedDyn = dynBase.map((sec, i) => applyTranslations(sec, dynOverlay[i]));
  return { ...base, dynamicSections: mergedDyn };
}
